import {
    useIsMutating,
    useMutation,
    useQueryClient,
    type InfiniteData,
    type QueryClient,
} from '@tanstack/react-query';
import { useCallback } from 'react';
import { queryKeyFn } from '~/backend/connector-next-api/connector-next-context';
import type {
    PatientDetailDto,
    PatientPageDto,
} from '~/backend/connector-next-api/connector-next-schemas';
import { PATIENT_LIST_QUERY_KEY } from './usePatientList';

type PatientWrite<TChange> = {
    /**
     * Sends `change`; `onError` runs if this write fails, `onSuccess` once it
     * has succeeded, while the sending component is mounted. Stable across
     * renders.
     */
    write: (
        change: TChange,
        onError?: () => void,
        onSuccess?: () => void
    ) => void;
    /** Whether the patient's id and version are known, without which no write is attempted. */
    canWrite: boolean;
    /** True while any write to the same patient is in flight, not only this one. */
    isSaving: boolean;
    /** True after a failed write, until the next attempt. */
    isError: boolean;
};

/** The patient a write addresses, and the version it is guarded by. */
type WriteTarget = {
    id?: string | null;
    version?: string | null;
};

/**
 * Runs one write to a patient, guarded by its version (If-Match), then
 * refreshes every patient query either way. Without an id or a version the
 * write is refused unsent, so it can never overwrite a change made meanwhile.
 * `send` resolves to the version the write made, which the cached patient
 * takes at once, so the next write is guarded by it before any refresh
 * lands. A patient whose refresh does not land gets back the version the
 * write was sent from, the one its cached values were read at, so the next
 * write conflicts rather than resending those values under a version they
 * do not belong to. While one write to a patient is in flight, every write
 * to it reports saving, so none is sent from a version that write is about
 * to replace.
 * `onFailure` runs on a failed write even when the component that sent it
 * unmounts before the failure is known.
 */
export function usePatientWrite<TChange>(
    patient: WriteTarget,
    send: (
        change: TChange,
        patientId: string,
        version: string
    ) => Promise<string>,
    onFailure?: () => void
): PatientWrite<TChange> {
    const queryClient = useQueryClient();
    const patientId = patient.id ?? undefined;
    const version = patient.version ?? undefined;
    const mutationKey = ['patient-write', patientId];
    const mutation = useMutation({
        mutationKey,
        mutationFn: (change: TChange) =>
            patientId === undefined || version === undefined
                ? Promise.reject(
                      new Error('The patient or its version is not known yet.')
                  )
                : send(change, patientId, version),
        // The target as it was when the write was sent, not as a later render sees it.
        onMutate: () =>
            patientId === undefined || version === undefined
                ? undefined
                : { patientId, version },
        onSuccess: (next, _change, sent) => {
            if (sent !== undefined) {
                setCachedPatientVersion(queryClient, sent.patientId, next);
            }
        },
        onError: onFailure,
        onSettled: async (next, _error, _change, sent) => {
            await queryClient.invalidateQueries({
                queryKey: ['api', 'patients'],
            });
            if (next !== undefined && sent !== undefined) {
                setCachedPatientVersion(
                    queryClient,
                    sent.patientId,
                    sent.version,
                    { onlyUnrefreshed: true }
                );
            }
        },
    });
    const writesInFlight = useIsMutating({ mutationKey });
    const { mutate } = mutation;
    const write = useCallback(
        (change: TChange, onError?: () => void, onSuccess?: () => void) =>
            mutate(change, { onError, onSuccess }),
        [mutate]
    );

    return {
        write,
        canWrite: patientId !== undefined && version !== undefined,
        isSaving: writesInFlight > 0,
        isError: mutation.isError,
    };
}

/**
 * Gives the cached detail of the patient, and its rows in every loaded list,
 * the version `version`. With `onlyUnrefreshed`, only the queries still
 * awaiting the refresh they were last asked for.
 */
function setCachedPatientVersion(
    queryClient: QueryClient,
    patientId: string,
    version: string,
    { onlyUnrefreshed = false } = {}
) {
    queryClient.setQueriesData<PatientDetailDto>(
        {
            queryKey: queryKeyFn({
                path: '/api/patients/{id}',
                operationId: 'patientsGetById',
                variables: { pathParams: { id: patientId } },
            }),
            exact: true,
            predicate: (query) => !onlyUnrefreshed || query.state.isInvalidated,
        },
        (detail) => detail && { ...detail, version }
    );
    queryClient.setQueriesData<InfiniteData<PatientPageDto>>(
        {
            queryKey: PATIENT_LIST_QUERY_KEY,
            predicate: (query) => !onlyUnrefreshed || query.state.isInvalidated,
        },
        (list) =>
            list && {
                ...list,
                pages: list.pages.map((page) => ({
                    ...page,
                    items: page.items?.map((row) =>
                        row.id === patientId ? { ...row, version } : row
                    ),
                })),
            }
    );
}
